import produceditemService from '../../../services/produceditemService';
import { loadDeferredTotal } from '../../../utils/deferredTotal';
import filterService from '../../../services/filterService';

import {
    setProducedItemLoading,
    setProducedItemError,
    setProducedItemList,
    setProducedItemCurrent,
    setProducedItemPagination,
    setProducedItemSort,
    setProducedItemFilters,
    setProducedItemDefaultFilter,
    setProducedItemSettings,
    clearProducedItemCurrent,
    clearProducedItemError,
    setProducedItemFilterLoading,
    changeProducedItemFilterCondition,
    addProducedItemFilterCondition,
    updateProducedItemFilterConditionRule,
    updateProducedItemFilterConditionValue,
    removeProducedItemFilterCondition,
    setProducedItemSelected,
    setProducedItemDownloading,
    resetProducedItemFiltersAndPagination
} from './produceditemSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().produceditem.isSettingsInitialized) {
            dispatch(setProducedItemLoading(true));
            const response = await produceditemService.getSettings();

            // Set the settings
            dispatch(setProducedItemSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setProducedItemDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setProducedItemError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setProducedItemLoading(false));
    }
};

export const fetchProduceditems = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().produceditem;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setProducedItemLoading(true));
        const response = await produceditemService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setProducedItemList(response));
        loadDeferredTotal(response, produceditemService, finalQueryParams, finalActiveFilter, {}, dispatch, setProducedItemPagination);
        return response;
    } catch (error) {
        console.error('Error in fetchProduceditems:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setProducedItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProducedItemLoading(false));
    }
};

export const fetchProducedItem = (id) => async (dispatch) => {
    try {
        dispatch(setProducedItemLoading(true));
        const response = await produceditemService.getOne(id);
        dispatch(setProducedItemCurrent(response.data));
    } catch (error) {
        dispatch(setProducedItemError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setProducedItemLoading(false));
    }
};

export const createProducedItem = (data) => async (dispatch) => {
    try {
        const response = await produceditemService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateProducedItem = (id, data) => async (dispatch) => {
    try {
        const response = await produceditemService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteProducedItem = (id) => async (dispatch) => {
    try {
        dispatch(setProducedItemLoading(true));
        const response = await produceditemService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setProducedItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProducedItemLoading(false));
    }
};

export const downloadProducedItem = (type) => async (dispatch, getState) => {
    try {
        const state = getState().produceditem;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setProducedItemDownloading(true));
        const response = await produceditemService.download(finalQueryParams, filter);
        dispatch(setProducedItemDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading produceditems:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setProducedItemError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProducedItemDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setProducedItemPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setProducedItemSort(sort));
};

export const searchProduceditems = (query) => (dispatch, getState) => {
    const currentFilters = getState().produceditem.filters;
    dispatch(setProducedItemFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setProducedItemDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeProducedItemFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addProducedItemFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateProducedItemFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateProducedItemFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeProducedItemFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setProducedItemFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().produceditem.settings;
            dispatch(setProducedItemSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setProducedItemFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setProducedItemFilterLoading(false));
        dispatch(setProducedItemError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setProducedItemSelected(selectedRows));
    };
};

export const clearCurrentProducedItem = () => {
    return (dispatch) => {
        dispatch(clearProducedItemCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetProducedItemFiltersAndPagination());
};